import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { getDefaultCurrency } from '../utils/currency';
import { Mail, Lock, User, Eye, EyeOff, Wand2, CheckCircle2, ShieldCheck } from 'lucide-react';
import ForgotPasswordModal from './modals/ForgotPasswordModal';

const passwordChecks = (password, minLength, t) => [
  { label: t('auth.pw_min', { count: minLength }), ok: (password || '').length >= minLength },
  { label: t('auth.pw_upper'), ok: /[A-Z]/.test(password || '') },
  { label: t('auth.pw_lower'), ok: /[a-z]/.test(password || '') },
  { label: t('auth.pw_digit'), ok: /\d/.test(password || '') },
];

const AuthModal = ({ isOpen, onClose, mode, onModeChange }) => {
  const { login, register, verify2FA, requestMagicLink } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [twoFA, setTwoFA] = useState(null); // { pendingToken, reason, message }
  const [twoFACode, setTwoFACode] = useState('');
  const [magicMode, setMagicMode] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [verifyNotice, setVerifyNotice] = useState(null);
  const [config, setConfig] = useState({ magic_link_enabled: true, min_password_length: 10 });

  useEffect(() => {
    if (!isOpen) return;
    axios
      .get('/auth/config')
      .then((res) => setConfig(res.data))
      .catch(() => {});
  }, [isOpen]);

  const reset = () => {
    setFormData({ name: '', email: '', password: '' });
    setTwoFA(null);
    setTwoFACode('');
    setMagicMode(false);
    setMagicSent(false);
    setVerifyNotice(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result;
      if (mode === 'login') {
        result = await login(formData.email, formData.password);
        if (result.requires2FA) {
          setTwoFA({ pendingToken: result.pendingToken, reason: result.reason, message: result.message });
          return;
        }
      } else {
        const failing = passwordChecks(formData.password, config.min_password_length, t).filter((c) => !c.ok);
        if (failing.length) {
          toast.error(t('auth.pw_too_weak', { missing: failing.map((f) => f.label.toLowerCase()).join(', ') }));
          return;
        }
        result = await register(
          formData.name,
          formData.email,
          formData.password,
          i18n.language,
          getDefaultCurrency(i18n.language)
        );
        if (result.requiresEmailVerification) {
          setVerifyNotice(result.message || t('auth.verify_email_title'));
          return;
        }
      }

      if (result.success) {
        onClose();
        reset();
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Auth error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await verify2FA(twoFA.pendingToken, twoFACode);
      if (result.success) {
        onClose();
        reset();
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await requestMagicLink(formData.email);
      if (result.success) setMagicSent(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const checks = passwordChecks(formData.password, config.min_password_length, t);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => {
        onClose();
        reset();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-center tracking-tight">
            {twoFA
              ? t('twofa.login_title')
              : verifyNotice
              ? t('auth.verify_email_title')
              : magicMode
              ? t('auth.magic_link_title')
              : mode === 'login'
              ? t('auth.welcome_back')
              : t('auth.create_account')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {mode === 'login' ? t('auth.welcome_back') : t('auth.create_account')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {verifyNotice ? (
            <div className="space-y-4 text-center" data-testid="verify-email-notice">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-mist">
                <Mail className="h-6 w-6 text-brand" />
              </div>
              <p className="text-sm text-zinc-600">{verifyNotice}</p>
              <p className="text-xs text-zinc-500">{t('auth.verify_email_hint')}</p>
              <Button
                variant="outline"
                className="w-full border-zinc-300"
                data-testid="resend-verification-button"
                onClick={async () => {
                  try {
                    await axios.post('/auth/resend-verification', { email: formData.email });
                    toast.success(t('auth.resend_sent'));
                  } catch (err) {
                    toast.error(err.response?.data?.detail || t('auth.resend_failed'));
                  }
                }}
              >
                {t('auth.resend_verification')}
              </Button>
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  onClose();
                  reset();
                }}
              >
                {t('auth.close')}
              </Button>
            </div>
          ) : twoFA ? (
            <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="twofa-login-form">
              <div className="space-y-2 text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-mist">
                  <ShieldCheck className="h-6 w-6 text-brand" />
                </div>
                <h3 className="text-lg font-semibold">
                  {twoFA.reason === 'new_device' ? t('auth.new_device_title') : t('twofa.login_title')}
                </h3>
                <p className="text-sm text-zinc-600">{twoFA.message || t('twofa.login_desc')}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="twofa-code">{t('twofa.code_label')}</Label>
                <Input
                  id="twofa-code"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  maxLength={6}
                  placeholder="000000"
                  className="text-center font-mono text-lg tracking-[0.5em]"
                  autoFocus
                  data-testid="twofa-login-code-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-brand hover:bg-brand-deep"
                disabled={loading || twoFACode.length !== 6}
                data-testid="twofa-login-verify-button"
              >
                {loading ? t('auth.processing') : t('twofa.verify_btn')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setTwoFA(null);
                  setTwoFACode('');
                }}
                data-testid="twofa-login-back-button"
              >
                {t('twofa.back')}
              </Button>
            </form>
          ) : magicMode ? (
            <div className="space-y-4" data-testid="magic-link-form">
              {magicSent ? (
                <div className="space-y-4 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-mist">
                    <CheckCircle2 className="h-6 w-6 text-brand" />
                  </div>
                  <p className="text-sm text-zinc-600" data-testid="magic-link-sent">
                    {t('auth.magic_link_sent', { email: formData.email })}
                  </p>
                  <Button variant="ghost" className="w-full" onClick={() => setMagicMode(false)}>
                    {t('auth.back_to_login')}
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleMagicLink} className="space-y-4">
                  <p className="text-sm text-zinc-600">{t('auth.magic_link_intro')}</p>
                  <div className="space-y-2">
                    <Label htmlFor="magic-email">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                      <Input
                        id="magic-email"
                        name="email"
                        type="email"
                        required
                        className="pl-10"
                        placeholder={t('auth.email_placeholder')}
                        value={formData.email}
                        onChange={handleInputChange}
                        data-testid="magic-link-email-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-brand hover:bg-brand-deep"
                    disabled={loading}
                    data-testid="magic-link-submit"
                  >
                    {loading ? t('auth.processing') : t('auth.magic_link_submit')}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => setMagicMode(false)}>
                    {t('auth.back_to_password')}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === 'register' && (
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('auth.full_name')}</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="pl-10"
                        placeholder={t('auth.full_name')}
                        value={formData.name}
                        onChange={handleInputChange}
                        data-testid="name-input"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="pl-10"
                      placeholder={t('auth.email')}
                      value={formData.email}
                      onChange={handleInputChange}
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('auth.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      className="pl-10 pr-10"
                      placeholder={t('auth.password')}
                      value={formData.password}
                      onChange={handleInputChange}
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {mode === 'register' && (
                    <ul className="space-y-1 pt-1" data-testid="password-requirements">
                      {checks.map((c) => (
                        <li
                          key={c.label}
                          className={`flex items-center gap-1.5 text-xs ${c.ok ? 'text-brand' : 'text-zinc-500'}`}
                        >
                          <CheckCircle2 className={`h-3.5 w-3.5 ${c.ok ? 'text-brand' : 'text-zinc-300'}`} />
                          {c.label}
                        </li>
                      ))}
                    </ul>
                  )}

                  {mode === 'login' && (
                    <div className="text-right">
                      <button
                        type="button"
                        className="text-sm text-brand hover:text-brand-deep hover:underline"
                        onClick={() => setShowForgotPassword(true)}
                        data-testid="forgot-password-link"
                      >
                        {t('auth.forgot_password')}
                      </button>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-brand hover:bg-brand-deep"
                  disabled={loading}
                  data-testid="submit-auth-button"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      {t('auth.processing')}
                    </div>
                  ) : mode === 'login' ? (
                    t('auth.login_button')
                  ) : (
                    t('auth.register_button')
                  )}
                </Button>
              </form>

              {config.magic_link_enabled && mode === 'login' && (
                <>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <Separator />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-zinc-500">{t('auth.or')}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full border-zinc-300 py-3 hover:border-brand hover:text-brand"
                    onClick={() => setMagicMode(true)}
                    data-testid="magic-link-button"
                  >
                    <Wand2 className="mr-2 h-4 w-4" />
                    {t('auth.magic_link_button')}
                  </Button>
                </>
              )}

              <div className="text-center text-sm text-zinc-600">
                {mode === 'login' ? (
                  <p>
                    {t('auth.no_account')}{' '}
                    <button
                      className="font-medium text-brand hover:text-brand-deep"
                      onClick={() => onModeChange('register')}
                      data-testid="switch-to-register"
                    >
                      {t('auth.register_link')}
                    </button>
                  </p>
                ) : (
                  <p>
                    {t('auth.have_account')}{' '}
                    <button
                      className="font-medium text-brand hover:text-brand-deep"
                      onClick={() => onModeChange('login')}
                      data-testid="switch-to-login"
                    >
                      {t('auth.login_link')}
                    </button>
                  </p>
                )}
              </div>

              {mode === 'register' && (
                <p className="text-center text-xs leading-relaxed text-zinc-500">
                  {t('auth.terms_text')}{' '}
                  <a href="/algemene-voorwaarden" className="text-brand hover:text-brand-deep">
                    {t('auth.terms_link')}
                  </a>{' '}
                  {t('auth.or').toLowerCase()}{' '}
                  <a href="/privacy-beleid" className="text-brand hover:text-brand-deep">
                    {t('auth.privacy_link')}
                  </a>
                </p>
              )}
            </>
          )}
        </div>
      </DialogContent>

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onBackToLogin={() => setShowForgotPassword(false)}
      />
    </Dialog>
  );
};

export default AuthModal;
